import { G2 } from '@antv/g2plot';
import { UserContext } from 'contexts/ConfigContext';
import { useContext, useEffect } from 'react';
import { formatNumWithKMB, getConfig } from 'utils/helperMethods';

type StackedGroupedChartProps = {
    containerId: string;
    height?: number;
    width?: number;
    data: any;
    xField: string;
    yField: string;
    yFieldMin?: number;
    yFieldMax?: number;
    colorField: string;
    groupingField: string;
    showTotalLabel?: boolean;
    totalField?: string;
    fieldToShowTotalValueLabel?: string;
    valuesOfTotalFieldToShowLabel?: string[];
    colorMapping: any;
    tooltipFormatter?: (xAxis: any, yAxis: any, color: any) => { name: string; value: string };
    totalLabelFormatter?: (val: number) => string;
};
const StackedGroupedChart = ({
    containerId,
    height = 400,
    width = 1000,
    data,
    xField,
    yField,
    yFieldMin = 0,
    yFieldMax = 10000,
    showTotalLabel = false,
    totalField = 'totalVal',
    fieldToShowTotalValueLabel = 'tierReference',
    valuesOfTotalFieldToShowLabel = [],
    colorField,
    groupingField,
    colorMapping,
    tooltipFormatter = (xAxis: any, yAxis: any, color: any) => {
        return {
            name: color,
            value: formatNumWithKMB(yAxis),
        };
    },
    totalLabelFormatter = (val: number) => {
        return formatNumWithKMB(val);
    },
}: StackedGroupedChartProps) => {
    const colorMap: any = colorMapping;
    const { appConfig } = useContext(UserContext);
    const commonSetting = getConfig('commonConfig', appConfig);
    const { graphFontSize, graphLabelColor, graphFontFamily,graphFontSizeAlt, graphLabelColorAlt } = commonSetting;

    useEffect(() => {
        const chart = new G2.Chart({
            container: containerId,
            height: height,
            width: width,
        });
        chart.data(data);
        chart.scale(yField, {
            min: yFieldMin,
            max: yFieldMax,
            nice: true,
        });
        chart.axis(xField, {
            label: {
                style: {
                    fontSize: graphFontSize,
                    fill: graphLabelColor,
                    fontFamily: graphFontFamily,
                },
            },
            tickLine: {
                alignTick: true,
            },
        });
        chart.axis(yField, {
            label: null,
            grid: {
                line: {
                    style: {
                        lineDash: [3, 3],
                    },
                },
            },
        });
        chart.tooltip({
            showMarkers: false,
            shared: true,
        });

        chart
            .interval()
            .position(`${xField}*${yField}`)
            .color(colorField, (val) => {
                return colorMap[val] as string;
            })
            .size(40)
            .label(`${totalField}*${fieldToShowTotalValueLabel}`, (val: number, tierValue: string) => {
                if(!showTotalLabel){
                    return
                }
                if (valuesOfTotalFieldToShowLabel.length && !valuesOfTotalFieldToShowLabel.includes(tierValue)) {
                    return null;
                }
                return {
                    position: 'top',
                    offset: 0,
                    content: totalLabelFormatter(val),
                    style: {
                        fontSize: graphFontSizeAlt,
                        fill: graphLabelColorAlt,
                        fontFamily: graphFontFamily,
                    },
                };
            })
            .tooltip(`${xField}*${yField}*${colorField}`, tooltipFormatter)
            .adjust([
                {
                    type: 'dodge',
                    dodgeBy: groupingField,
                    marginRatio: 0.3,
                },
                {
                    type: 'stack',
                },
            ]);

        chart.legend(false);
        chart.interaction('active-region');
        if (window) {
            try {
                chart.render();
            } catch (e) {
                console.log('This is chart render block error', e);
            }
        }
        return () => {
            chart.destroy();
        };
    }, [width, data, xField, yField, colorField, groupingField, containerId, height, tooltipFormatter]);
    return <div id={containerId}></div>;
};

export default StackedGroupedChart;
